/**
 * Карточка цикла в загрузке (agroai-6al.49): тела блоков для `DataGate`.
 * Рамки Card и их заголовки на странице настоящие, поэтому здесь только то,
 * что внутри рамки, — в высоту готового содержимого.
 */
import { defineMessages } from '../../i18n/define';
import { useT } from '../../i18n/hooks';
import { Grid } from '../ui/Card';
import { Skeleton, SkeletonKpi, SkeletonList, SkeletonRegion, SkeletonTable, SkeletonText } from '../ui/Skeleton';

const m = defineMessages({
  kpi: {
    id: 'cycle.skeleton.kpi',
    defaultMessage: 'показатели агроцикла',
    description: 'Подпись для программ экрана (aria-label) пока загружается ряд KPI агроцикла; винительный падеж после «Загружаем»',
  },
  chronology: {
    id: 'cycle.skeleton.chronology',
    defaultMessage: 'хронологию работ',
    description: 'Подпись для программ экрана (aria-label) пока загружается хронология работ агроцикла; винительный падеж после «Загружаем»',
  },
  extraWorks: {
    id: 'cycle.skeleton.extraWorks',
    defaultMessage: 'добровольные работы',
    description: 'Подпись для программ экрана (aria-label) пока загружаются добровольные работы агроцикла; винительный падеж после «Загружаем»',
  },
  works: {
    id: 'cycle.skeleton.works',
    defaultMessage: 'работы агроцикла',
    description: 'Подпись для программ экрана (aria-label) пока загружается таблица работ агроцикла; винительный падеж после «Загружаем»',
  },
  events: {
    id: 'cycle.skeleton.events',
    defaultMessage: 'события агроцикла',
    description: 'Подпись для программ экрана (aria-label) пока загружается лента событий агроцикла; винительный падеж после «Загружаем»',
  },
  water: {
    id: 'cycle.skeleton.water',
    defaultMessage: 'воду агроцикла',
    description: 'Подпись для программ экрана (aria-label) пока загружается блок «Вода агроцикла»; винительный падеж после «Загружаем»',
  },
});

export function CycleKpiSkeleton() {
  const t = useT();
  return (
    <SkeletonRegion label={t(m.kpi)}>
      {/* Ряд KPI цикла выше обычного (длинная подпись окон): резерв высоты. */}
      <div className="cyc-kpi-skel"><Grid cols={4}>{Array.from({ length: 4 }, (_, i) => <SkeletonKpi key={i} quiet />)}</Grid></div>
    </SkeletonRegion>
  );
}

/** Хронология работ: шкала и строки-полосы. */
export function CycleChronologySkeleton() {
  const t = useT();
  return (
    <SkeletonRegion label={t(m.chronology)} minHeight={520}>
      <span className="skel-text" aria-hidden="true">
        <Skeleton width="100%" className="skel--label" />
        {Array.from({ length: 8 }, (_, i) => {
          const left = (i * 11) % 40;
          return <Skeleton key={i} variant="block" width={`${Math.min(20 + ((i * 23) % 45), 100 - left)}%`} height={22} style={{ marginLeft: `${left}%`, marginBlock: 'var(--sp-3)' }} />;
        })}
      </span>
    </SkeletonRegion>
  );
}

export function CycleExtraWorksSkeleton() {
  const t = useT();
  return (
    <SkeletonRegion label={t(m.extraWorks)} minHeight={44}>
      <SkeletonText lines={2} />
    </SkeletonRegion>
  );
}

export function CycleWorksTableSkeleton() {
  const t = useT();
  return <SkeletonTable label={t(m.works)} rows={7} cols={6} />;
}

export function CycleEventsSkeleton() {
  const t = useT();
  return <SkeletonList label={t(m.events)} items={5} tone="soft" />;
}

/** «Вода цикла»: четыре факта слева, столбики справа. */
export function CycleWaterSkeleton() {
  const t = useT();
  return (
    <SkeletonRegion label={t(m.water)} minHeight={150}>
      <div className="cyc-water" aria-hidden="true">
        <SkeletonText lines={4} lastWidth="100%" />
        <Skeleton variant="block" width={260} height={110} />
      </div>
    </SkeletonRegion>
  );
}
